import { useCallback, useEffect, useState } from 'react'
import { deleteLead, listLeads, setLeadHandled, type Lead } from '../../services/superadmin'
import { Spinner } from '../Spinner'
import { TrashIcon } from '../icons'

/**
 * "Sizi arayalım" talepleri, tanıtım sitesinden.
 *
 * Aranan talep önce işaretleniyor: kimi aradığın ve ne zaman yazdığı
 * sonradan da lazım olabilir. İşaretliler listenin içinde soluk kalıyor.
 * Gereksiz olanlar çöp kutusuyla silinebiliyor (kullanıcı, 11 Eki 2026);
 * silme kalıcı olduğu için ikinci dokunuşla onaylanıyor.
 */
export function LeadsPanel() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  /** Silinmesi onay bekleyen talep: çöp kutusuna bir kez basılınca. */
  const [confirmId, setConfirmId] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setError(null)
    try {
      setLeads(await listLeads())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Talepler okunamadı.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const toggle = async (lead: Lead) => {
    // Önce ekranda, sonra sunucuda: tik anında görünsün.
    setLeads((all) => all.map((l) => (l.id === lead.id ? { ...l, handled: !l.handled } : l)))
    try {
      await setLeadHandled(lead.id, !lead.handled)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kaydedilemedi.')
      void reload()
    }
  }

  const remove = async (lead: Lead) => {
    setConfirmId(null)
    setLeads((all) => all.filter((l) => l.id !== lead.id))
    try {
      await deleteLead(lead.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Silinemedi.')
      void reload()
    }
  }

  if (loading) return <Spinner label="Talepler yükleniyor…" />

  const open = leads.filter((l) => !l.handled).length

  return (
    <div className="leads">
      <p className="leads__summary">
        {leads.length === 0
          ? 'Henüz talep yok. Tanıtım sitesindeki "Sizi arayalım" formundan ve denemedeki patronların "devam etmek istiyorum" düğmesinden gelenler burada görünür.'
          : `${open} aranmayı bekliyor, toplam ${leads.length}`}
      </p>
      {error && <p className="superadmin__error">{error}</p>}
      <ul className="leads__list">
        {leads.map((l) => (
          <li key={l.id} className={`leads__item ${l.handled ? 'is-done' : ''}`}>
            <div className="leads__main">
              <strong>
                {l.kind === 'devam' && <span className="leads__kind">Devam talebi</span>}
                {l.name}
              </strong>
              {l.business && <span className="leads__biz">{l.business}</span>}
              {l.phone && <span className="leads__phone">{l.phone}</span>}
              {l.note && <span className="leads__note">{l.note}</span>}
            </div>
            <div className="leads__side">
              <span className="leads__date">
                {new Date(l.created_at).toLocaleString('tr-TR', {
                  day: 'numeric',
                  month: 'short',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
              {confirmId === l.id ? (
                <div className="leads__confirm">
                  <span>Silinsin mi?</span>
                  <button type="button" className="btn btn--sm btn--ghost" onClick={() => setConfirmId(null)}>
                    Vazgeç
                  </button>
                  <button type="button" className="btn btn--sm btn--danger" onClick={() => void remove(l)}>
                    Sil
                  </button>
                </div>
              ) : (
                <div className="leads__actions">
                  <label className="leads__done">
                    <input type="checkbox" checked={l.handled} onChange={() => void toggle(l)} />
                    Arandı
                  </label>
                  <button
                    type="button"
                    className="leads__delete"
                    aria-label="Talebi sil"
                    title="Talebi sil"
                    onClick={() => setConfirmId(l.id)}
                  >
                    <TrashIcon />
                  </button>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
    </div>
  )
}
